import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import NewSuggestionModal from "./NewSuggestionModal";
import ConvertSuggestionModal from "./ConvertSuggestionModal";
import { useTripRealtimeSync } from "../lib/useTripRealtimeSync";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";

interface Suggestion {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  user_id: string;
  created_at: string | null;
  vote_count: number;
  has_voted: boolean;
  accepted_at: string | null;
  program_item_id: string | null;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

interface TripSuggestionsProps {
  tripId: string;
  userId: string;
  isOrganizer: boolean;
  tripStartDate: string;
  tripEndDate: string;
  showSuccess?: boolean;
  onClearSuccess?: () => void;
  onProgramItemCreated?: () => void;
}

export default function TripSuggestions({
  tripId,
  userId,
  isOrganizer,
  tripStartDate,
  tripEndDate,
  showSuccess,
  onClearSuccess,
  onProgramItemCreated,
}: TripSuggestionsProps) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [realtimeReady, setRealtimeReady] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState<
    Suggestion | undefined
  >(undefined);
  const [convertingSuggestion, setConvertingSuggestion] = useState<
    Suggestion | undefined
  >(undefined);
  const { showConfirm } = useConfirmDialog();

  useEffect(() => {
    if (tripId && user) {
      loadSuggestions();
      setRealtimeReady(true); // erst dann realtime aktivieren
    }
  }, [tripId, user]);

  const loadSuggestions = async () => {
    setLoading(true);

    const { data: suggestionsData, error: suggestionsError } = await supabase
      .from("trip_suggestions")
      .select("*, profiles:user_id(first_name, last_name)")
      .eq("trip_id", tripId);

    if (suggestionsError || !suggestionsData) {
      console.error("❌ Fehler beim Laden der Vorschläge:", suggestionsError);
      setLoading(false);
      return;
    }

    // IDs extrahieren
    const suggestionIds = suggestionsData.map((s) => s.id);

    let votes: { suggestion_id: string; user_id: string }[] = [];

    if (suggestionIds.length > 0) {
      const { data: votesData, error: votesError } = await supabase
        .from("trip_suggestion_votes")
        .select("suggestion_id, user_id")
        .in("suggestion_id", suggestionIds);

      if (votesError) {
        console.error("❌ Fehler beim Laden der Votes:", votesError);
      } else {
        votes = votesData ?? [];
      }
    }

    const suggestions = suggestionsData.map((sugg) => {
      const suggestionVotes = votes.filter((v) => v.suggestion_id === sugg.id);
      return {
        ...sugg,
        vote_count: suggestionVotes.length,
        has_voted: suggestionVotes.some((v) => v.user_id === userId),
        accepted_at: (sugg as any).accepted_at ?? null,
        program_item_id: (sugg as any).program_item_id ?? null,
      };
    });

    suggestions.sort((a, b) => b.vote_count - a.vote_count);
    setSuggestions(suggestions);
    setLoading(false);
  };

  const handleDelete = async (suggestionId: string) => {
    const confirmed = await showConfirm({
      title: "Vorschlag löschen",
      message: "Möchtest du diesen Vorschlag wirklich löschen?",
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
      icon: "warning",
    });

    if (!confirmed) return;

    const { error } = await supabase
      .from("trip_suggestions")
      .delete()
      .eq("id", suggestionId);

    if (error) {
      console.error("Löschen fehlgeschlagen:", error?.message);
    } else {
      loadSuggestions();
    }
  };

  // Realtime erst, wenn bereit
  useTripRealtimeSync<Suggestion>({
    tripId: realtimeReady ? tripId : null,
    table: "trip_suggestions",
    onRefresh: loadSuggestions,
  });

  useTripRealtimeSync<any>({
    tripId: realtimeReady ? tripId : null,
    table: "trip_suggestion_votes",
    getId: (v) => `${v.suggestion_id}-${v.user_id}`,
    onRefresh: loadSuggestions,
  });

  const handleVoteToggle = async (suggestion: Suggestion) => {
    if (!user?.id) return;

    if (suggestion.has_voted) {
      // 👎 Bereits gevotet → Entfernen (Downvote)
      const { error } = await supabase
        .from("trip_suggestion_votes")
        .delete()
        .eq("suggestion_id", suggestion.id)
        .eq("user_id", user.id);

      if (error) {
        console.error("Downvote fehlgeschlagen:", error);
      }
    } else {
      // 👍 Noch nicht gevotet → Upvote
      const { error } = await supabase.from("trip_suggestion_votes").insert({
        suggestion_id: suggestion.id,
        user_id: user.id,
      });

      if (error) {
        console.error("Upvote fehlgeschlagen:", error);
      }
    }

    // In beiden Fällen: reload
    loadSuggestions();
  };

  return (
    <div className="mt-6">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Vorschläge &amp; Ideen
      </h3>

      {showSuccess && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2.5 rounded-xl mb-4 text-sm">
          ✅ Vorschlag wurde gespeichert!
        </div>
      )}

      {loading && (
        <p className="text-slate-600 text-sm px-1">Lade Vorschläge…</p>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="text-center py-10">
          <p className="text-slate-500 text-sm">🤔 Noch keine Ideen eingereicht</p>
          <p className="text-slate-700 text-xs mt-1">
            Hast du eine Idee? Reiche deinen Vorschlag ein – andere können abstimmen ✨
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {suggestions.map((sugg) => {
          const authorName = sugg.profiles
            ? `${sugg.profiles.first_name ?? ""} ${sugg.profiles.last_name ?? ""}`.trim()
            : "";

          return (
            <div
              key={sugg.id}
              className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors flex flex-col gap-2"
            >
              {/* Accepted-Badge */}
              {sugg.accepted_at && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 self-start">
                  ✓ Als Programmpunkt übernommen
                </span>
              )}

              {/* Category + Author */}
              <div className="flex items-center gap-2 flex-wrap">
                {sugg.category && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#1e2639] text-slate-400 shrink-0">
                    {sugg.category}
                  </span>
                )}
                {authorName && (
                  <span className="text-[10px] text-slate-600 truncate">
                    {authorName}
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 className="text-sm font-semibold text-slate-100 leading-snug">
                {sugg.title}
              </h3>

              {/* Description */}
              {sugg.description && (
                <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">
                  {sugg.description}
                </p>
              )}

              {/* Footer: actions + vote */}
              <div className="flex items-center justify-between mt-auto pt-2">
                {isOrganizer ? (
                  <div className="flex gap-3">
                    {!sugg.accepted_at && (
                      <button
                        onClick={() => setConvertingSuggestion(sugg)}
                        className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors"
                      >
                        ✓ Annehmen
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingSuggestion(sugg);
                        setModalOpen(true);
                      }}
                      className="text-[11px] text-slate-600 hover:text-slate-300 transition-colors"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDelete(sugg.id)}
                      className="text-[11px] text-slate-600 hover:text-red-400 transition-colors"
                    >
                      Löschen
                    </button>
                  </div>
                ) : (
                  <div />
                )}

                <button
                  onClick={() => handleVoteToggle(sugg)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    sugg.has_voted
                      ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                      : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
                  }`}
                >
                  👍 {sugg.vote_count}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <NewSuggestionModal
          tripId={tripId!}
          userId={user!.id}
          suggestion={editingSuggestion}
          onClose={(updated?: any) => {
            setModalOpen(false);
            setEditingSuggestion(undefined);
            if (updated) loadSuggestions();
          }}
        />
      )}

      {convertingSuggestion && (
        <ConvertSuggestionModal
          tripId={tripId}
          userId={user!.id}
          suggestion={convertingSuggestion}
          tripStartDate={tripStartDate}
          tripEndDate={tripEndDate}
          onClose={(converted?: boolean) => {
            setConvertingSuggestion(undefined);
            if (converted) {
              loadSuggestions();
              onProgramItemCreated?.();
            }
          }}
        />
      )}
    </div>
  );
}
